/**
 * Image preloader utility functions
 */

/**
 * Preload an array of images
 * @param {Array<string|Object>} images - Array of image paths or objects with src property
 * @param {Function} [onProgress] - Optional callback for progress updates
 * @returns {Promise<Array>} - Promise that resolves when all images are loaded
 */
export const preloadImages = (images, onProgress = null) => {
  if (!images || !images.length) {
    return Promise.resolve([]);
  }

  const totalImages = images.length;
  let loadedCount = 0;

  // Create an array of promises for each image
  const promises = images.map((image) => {
    const src = typeof image === 'string' ? image : image.src;
    
    return new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => {
        loadedCount++;
        
        if (onProgress) {
          onProgress(loadedCount / totalImages);
        }
        
        resolve(src);
      };
      
      img.onerror = () => {
        loadedCount++;
        
        if (onProgress) {
          onProgress(loadedCount / totalImages);
        }
        
        // Resolve anyway to not block the entire promise chain
        resolve(null);
      };
      
      img.src = src;
    });
  });
  
  return Promise.all(promises);
};

/**
 * Preload the next image in a carousel
 * @param {Array<string|Object>} images - Array of image paths or objects with src property
 * @param {number} currentIndex - Current image index
 * @param {number} [buffer=2] - Number of images to preload ahead
 */
export const preloadNextImages = (images, currentIndex, buffer = 2) => {
  if (!images || !images.length) return;
  
  const totalImages = images.length;
  
  // Preload the next few images
  for (let i = 1; i <= buffer; i++) {
    const nextIndex = (currentIndex + i) % totalImages;
    const src = typeof images[nextIndex] === 'string' 
      ? images[nextIndex] 
      : images[nextIndex].src;
    
    const img = new Image();
    img.src = src;
  }
};

/**
 * Preload all responsive image variations
 * @param {string} src - Base image source
 */
export const preloadResponsiveImage = (src) => {
  if (!src) return;
  
  // Extract base path and extension
  const extension = src.split('.').pop();
  const basePath = src.substring(0, src.lastIndexOf('.'));
  
  // Widths for responsive images
  const widths = [640, 768, 1024, 1280, 1536];
  
  // Preload each width variant
  widths.forEach(width => {
    const responsiveSrc = `${basePath}-${width}.${extension}`;
    const img = new Image();
    img.src = responsiveSrc;
  });
};
